import {
  type ActionFromReducersMapObject,
  combineReducers,
  configureStore,
  createSelector,
  type PayloadAction,
  type StateFromReducersMapObject,
} from '@reduxjs/toolkit'
import { useDispatch, useSelector } from 'react-redux'
import { tmsApi } from '#api/tmsApi'
import { collectMetadataForRequest } from '#features/DebugSettings/collectMetadataForRequest'
import { apiRequestPerformanceObserver } from '#features/DebugSettings/RequestMonitor/ApiRequestPerformanceObserver'
import { componentConfigurationActions } from '#store/componentConfiguration'
import { debugActions } from '#store/debugConfiguration'
import { layoutConfigurationActions } from '#store/layoutConfiguration'
import { makeAppLocalStorage } from '#store/middlewares/appLocalStorage'
import { makeKeepAliveConfigurationSessions } from '#store/middlewares/keepAliveConfigurationSessions'
import { makeUserDataPersistence } from '#store/middlewares/userDataPersistence'
import { requestObserverSlice } from '#store/requestObserverSlice'
import { sharedSlices } from '#store/sharedBaseStore'
import { userDataActions, userDataSlice } from '#store/slices/userDataSlice'
import { autolockConfiguration } from './middlewares/autolockConfiguration'
import { makeConfigurationRequestMiddleware } from './middlewares/configurationRequestMiddleware'
import { makeManCodeTracker } from './middlewares/manCodeTracker'
import { type TruckConfiguration, truckConfigurationActions, truckConfigurationSlice } from './slices/configurationSlice'
import { featureSlice, featureSliceActions } from './slices/featureSlice'
import { localConfigSlice } from './slices/localConfigSlice'
import { addUserSessionNotifier } from './userSessionNotifier'

const reducers = {
  ...sharedSlices,
  [tmsApi.reducerPath]: tmsApi.reducer,
  localConfig: localConfigSlice,
  userData: userDataSlice,
  features: featureSlice.reducer,
  truckConfigurations: truckConfigurationSlice.reducer,
  requestObserver: requestObserverSlice.reducer,
}

const rootReducer = combineReducers(reducers)
const hookedReducer = (state: RootState | Partial<RootState> | undefined, action: RootActions): RootState => {
  // special kind of reducer that allows for fully replacing the entire state
  if (action.type === '__system/replace') {
    const message = action as PayloadAction<RootState>
    return message.payload
  }
  return rootReducer(state, action)
}

const allActions = {
  ...componentConfigurationActions,
  ...debugActions,
  ...layoutConfigurationActions,
  ...truckConfigurationActions,
  ...featureSliceActions,
  ...userDataActions,
}

export type RootState = StateFromReducersMapObject<typeof reducers>
export type RootActions = ActionFromReducersMapObject<typeof reducers>
export type RootStore = typeof store

// biome-ignore lint/style/noNonNullAssertion: poor attempt to late initialize the root store
export let store: Awaited<ReturnType<typeof lateInitializeStore>> = undefined!

export const lateInitializeStore = async () => {
  const appStorage = await makeAppLocalStorage()
  const state = await appStorage.loadStoredState(hookedReducer(undefined, { type: '@@INIT' }))

  const preloadStore = configureStore({
    preloadedState: state,
    reducer: hookedReducer,
    devTools: {
      name: 'MAIC Frontend',
      trace: true,
      traceLimit: 25,
      actionCreators: allActions,

      actionsDenylist: [
        // all of redux-query actions are ignored (extremly noisy)
        ...Object.values(tmsApi.internalActions).map((x) => x.type),
        `${tmsApi.reducerPath}/executeQuery`,
        `${tmsApi.reducerPath}/executeQuery/fulfilled`,
        `${tmsApi.reducerPath}/executeMutation/pending`,
        `${tmsApi.reducerPath}/executeMutation/fulfilled`,

        // ignore all request observer actions, like startApiRequest, finishApiRequest, etc
        ...Object.values(requestObserverSlice.actions).map((x) => x.type),
      ],
    },
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware().concat(
        tmsApi.middleware,
        appStorage.localStorageListener.middleware,
        autolockConfiguration().middleware,
        makeUserDataPersistence().middleware,
        makeManCodeTracker().middleware,
        makeConfigurationRequestMiddleware().middleware,
        makeKeepAliveConfigurationSessions().middleware,
      ),
  })
  store = preloadStore
  apiRequestPerformanceObserver.connectStore(store)
  apiRequestPerformanceObserver.registerApiMetadataCollector(collectMetadataForRequest)

  addUserSessionNotifier(store)

  // @ts-expect-error making a window global function to inject configurations
  window.__placeConfiguration = (configuration: TruckConfiguration) => {
    store.dispatch({
      type: 'truck-configurations/_placeConfiguration',
      payload: {
        configuration: configuration,
        reason: 'mock',
      },
    } satisfies ReturnType<(typeof truckConfigurationSlice)['actions']['_placeConfiguration']>)
  }

  // @ts-expect-error (poorly patching in redux accessible through global window - used to access the store in a test driver)
  window.redux = () => {
    return store
  }

  return preloadStore
}

export type AppDispatch = typeof store.dispatch
export const useAppDispatch = useDispatch.withTypes<AppDispatch>()
export const useAppSelector = useSelector.withTypes<RootState>()
export const createAppSelector = createSelector.withTypes<RootState>()
